import {
  Activity,
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  CircleHelp,
  Filter,
  MousePointer2,
  RefreshCw,
  Users,
  X,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { API_ENDPOINTS } from "../../../../constants/common";
import { ANALYTICS_EVENTS } from "../../../../analytics/events";
import { apiClient } from "../../../../utils";

type AnalyticsActorType = "all" | "authenticated" | "anonymous";
type MetricKey = "events" | "visitors" | "visits";
type Category =
  | "all"
  | "calculations"
  | "market"
  | "identity"
  | "revenue"
  | "system";
type SummaryStatus = "actual" | "updating" | "error";
type RefreshStatus = "queued" | "processing" | "completed" | "failed";

type EventMetrics = {
  events: number;
  unique_visitors: number;
  unique_visits: number;
};

type MetrikaEvent = {
  name: string;
  label?: string;
  description?: string;
  all: EventMetrics;
  authenticated: EventMetrics;
  anonymous: EventMetrics;
};

type MetrikaDailyEvent = {
  name: string;
  label?: string;
  description?: string;
  all: number;
  authenticated: number;
  anonymous: number;
};

type MetrikaDailyDynamics = {
  date: string;
  all: number;
  authenticated: number;
  anonymous: number;
  events?: MetrikaDailyEvent[];
};

type MetrikaSummary = {
  period: { date_from: string; date_to: string };
  updated_at: string;
  status: SummaryStatus;
  segment: AnalyticsActorType;
  totals: EventMetrics & { active_actions: number };
  events: MetrikaEvent[];
  daily_dynamics: MetrikaDailyDynamics[];
};

type RefreshStatusData = {
  refresh_id: string;
  status: RefreshStatus;
  period: { date_from: string; date_to: string };
  summary?: MetrikaSummary;
  message?: string;
};

type MetrikaPeriodLimits = {
  min_date: string;
  max_date: string;
  max_days_per_request: number;
  period: { date_from: string; date_to: string };
  possible: boolean;
  max_possible_day_quantity: number;
};

const numberFormat = new Intl.NumberFormat("ru-RU");
const JSON_HEADERS = { Accept: "application/json" };

const ACTOR_LABELS: Record<AnalyticsActorType, string> = {
  all: "Все пользователи",
  authenticated: "Залогиненные",
  anonymous: "Незалогиненные",
};

const METRIC_LABELS: Record<MetricKey, string> = {
  events: "События",
  visitors: "Уникальные посетители",
  visits: "Уникальные визиты",
};

const METRIC_DESCRIPTIONS: Record<MetricKey, string> = {
  events:
    "Сколько раз действие было зафиксировано. Один и тот же человек может выполнить его несколько раз.",
  visitors:
    "Сколько разных браузеров выполнили действие хотя бы раз. Это не число аккаунтов: один человек в другом браузере будет посчитан отдельно.",
  visits:
    "Сколько отдельных визитов в сервис содержали это действие. Один браузер в разные заходы может дать несколько визитов.",
};

const ACTOR_DESCRIPTIONS: Record<AnalyticsActorType, string> = {
  all: "Все действия за выбранный период — до входа в аккаунт и после него.",
  authenticated:
    "Только действия, для которых в момент события пользователь был авторизован.",
  anonymous:
    "Только действия без авторизации: до входа, после выхода или на публичных страницах.",
};

const CATEGORY_LABELS: Record<Category, string> = {
  all: "Все действия",
  calculations: "Расчёты и шаринг",
  market: "Маркет",
  identity: "Вход и регистрация",
  revenue: "Оплата и тарифы",
  system: "Системные события",
};

const EVENT_LABELS: Record<string, string> = {
  auth_sign_in_succeeded: "Успешный вход",
  auth_sign_up_succeeded: "Успешная регистрация",
  calculation_ready: "Готовый расчёт",
  calculation_share_requested: "Запрос ссылки",
  calculation_share_created: "Ссылка создана",
  calculation_share_failed: "Ошибка создания ссылки",
  calculation_link_copied: "Ссылка скопирована",
  calculation_native_share_succeeded: "Нативный шаринг выполнен",
  calculation_native_share_failed: "Ошибка нативного шаринга",
  calculation_attach_requested: "Прикрепление к сделке: начало",
  calculation_attached_to_deal: "Прикрепление к сделке: успех",
  calculation_attach_failed: "Прикрепление к сделке: ошибка",
  shared_calculation_opened: "Публичный расчёт открыт",
  shared_calculation_contact_clicked: "Контакт на публичной странице",
  feature_access_denied: "Доступ к функции запрещён",
  market_opened: "Маркет открыт",
  market_listings_loaded: "Список объявлений загружен",
  market_listing_opened: "Открыто объявление",
  market_listing_viewed: "Просмотр объявления",
  market_listing_create_started: "Начато создание объявления",
  market_listing_created: "Объявление создано",
  market_listing_shared: "Объявлением поделились",
  market_listing_updated: "Объявление обновлено",
  market_shared_listing_opened: "Публичная ссылка объявления открыта",
  market_contact_clicked: "Клик по контакту",
  market_favorite_added: "Добавлено в избранное",
  market_favorite_removed: "Удалено из избранного",
  payment_opened: "Открыта оплата",
  payment_started: "Начата оплата",
  subscription_plan_selected: "Выбран тариф",
  market_listings_load_failed: "Ошибка загрузки объявлений",
  market_search_applied: "Применён поиск объявлений",
  market_filter_applied: "Применён фильтр объявлений",
  market_listing_save_failed: "Ошибка сохранения объявления",
  market_listing_create_failed: "Ошибка создания объявления",
  market_listing_publish_toggled: "Изменена публикация объявления",
  market_listing_share_failed: "Ошибка шаринга объявления",
  landing_phone_clicked: "Нажат телефон на лендинге",
  landing_lead_submitted: "Заявка с лендинга отправлена",
  assignment_created: "Переуступка создана",
  assignment_updated: "Переуступка обновлена",
};

const EVENT_DESCRIPTIONS: Record<string, string> = {
  auth_sign_in_succeeded: "Пользователь успешно вошёл в аккаунт.",
  auth_sign_up_succeeded: "Пользователь успешно зарегистрировал аккаунт.",
  calculation_ready: "Пользователь успешно получил результат расчёта.",
  calculation_share_requested: "Пользователь начал создание ссылки на расчёт.",
  calculation_share_created: "Ссылка для общего доступа к расчёту была создана.",
  calculation_share_failed: "При создании ссылки на расчёт произошла ошибка.",
  calculation_link_copied: "Ссылка на расчёт была скопирована.",
  calculation_native_share_succeeded: "Расчётом поделились через системное меню устройства.",
  calculation_native_share_failed: "Не удалось поделиться расчётом через системное меню устройства.",
  calculation_attach_requested: "Пользователь начал прикрепление расчёта к сделке.",
  calculation_attached_to_deal: "Расчёт успешно прикреплён к сделке.",
  calculation_attach_failed: "Не удалось прикрепить расчёт к сделке.",
  shared_calculation_opened: "Открыта публичная страница расчёта по ссылке.",
  shared_calculation_contact_clicked: "На публичной странице расчёта нажали контакт.",
  feature_access_denied: "Пользователь попытался открыть функцию, недоступную его тарифу или правам.",
  market_opened: "Пользователь открыл раздел объявлений — Маркет.",
  market_listings_loaded: "Список объявлений успешно загружен.",
  market_listings_load_failed: "Список объявлений не удалось загрузить.",
  market_search_applied: "Пользователь выполнил поиск по объявлениям.",
  market_filter_applied: "Пользователь применил фильтры к списку объявлений.",
  market_listing_opened: "Открыта карточка объявления в Маркете.",
  market_listing_viewed: "Объявление было просмотрено.",
  market_favorite_added: "Объявление добавлено в избранное.",
  market_favorite_removed: "Объявление удалено из избранного.",
  market_listing_create_started: "Пользователь начал создавать объявление.",
  market_listing_created: "Новое объявление успешно создано.",
  market_listing_updated: "Существующее объявление успешно обновлено.",
  market_listing_save_failed: "Не удалось сохранить изменения объявления.",
  market_listing_create_failed: "Не удалось создать новое объявление.",
  market_listing_publish_toggled: "Статус публикации объявления был изменён.",
  market_listing_shared: "Пользователь поделился объявлением.",
  market_listing_share_failed: "Не удалось поделиться объявлением.",
  market_shared_listing_opened: "Открыто публичное объявление по ссылке.",
  market_contact_clicked: "Пользователь нажал контакт продавца в объявлении.",
  payment_opened: "Пользователь открыл экран оплаты или выбора тарифа.",
  payment_started: "Пользователь начал оформление оплаты.",
  subscription_plan_selected: "Пользователь выбрал тарифный план.",
  landing_phone_clicked: "На лендинге нажали номер телефона.",
  landing_lead_submitted: "Пользователь отправил заявку с лендинга.",
  assignment_created: "Пользователь создал карточку переуступки.",
  assignment_updated: "Пользователь обновил карточку переуступки.",
};

const getEventLabel = (event: {
  name: string;
  label?: string;
}) =>
  event.label?.trim() || EVENT_LABELS[event.name] || `Событие «${event.name}»`;

const getEventDescription = (event: {
  name: string;
  label?: string;
  description?: string;
}) =>
  event.description?.trim() ||
  EVENT_DESCRIPTIONS[event.name] ||
  `Продуктовое событие «${getEventLabel(event)}» (${event.name}).`;

const categoryOf = (event: MetrikaEvent): Category => {
  if (
    event.name.startsWith("calculation_") ||
    event.name.startsWith("shared_calculation")
  )
    return "calculations";
  if (event.name.startsWith("market_")) return "market";
  if (event.name.startsWith("auth_")) return "identity";
  if (
    event.name.startsWith("payment_") ||
    event.name.startsWith("subscription_")
  )
    return "revenue";
  return "system";
};

const eventMetric = (
  event: MetrikaEvent,
  actor: AnalyticsActorType,
  metric: MetricKey,
) => {
  if (metric === "events") return event[actor].events;
  if (metric === "visitors") return event[actor].unique_visitors;
  return event[actor].unique_visits;
};

const ClickTooltip = ({
  label,
  content,
  placement = "top",
}: {
  label: string;
  content: React.ReactNode;
  placement?: "top" | "bottom";
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    left: 16,
    top: 16,
    width: 288,
  });

  const updateTooltipPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const width = Math.min(288, Math.max(window.innerWidth - 32, 0));
    const left = Math.min(
      Math.max(rect.left, 16),
      Math.max(window.innerWidth - width - 16, 16),
    );
    setTooltipPosition({
      left,
      top: placement === "top" ? rect.top - 8 : rect.bottom + 8,
      width,
    });
  }, [placement]);

  useEffect(() => {
    if (!isOpen) return;
    updateTooltipPosition();
    window.addEventListener("resize", updateTooltipPosition);
    window.addEventListener("scroll", updateTooltipPosition, true);
    return () => {
      window.removeEventListener("resize", updateTooltipPosition);
      window.removeEventListener("scroll", updateTooltipPosition, true);
    };
  }, [isOpen, updateTooltipPosition]);

  return (
    <span className="inline-flex shrink-0 align-middle">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={isOpen}
        onClick={(clickEvent) => {
          clickEvent.stopPropagation();
          if (!isOpen) updateTooltipPosition();
          setIsOpen((value) => !value);
        }}
        className="inline-flex rounded-full text-slate-400 outline-none transition hover:text-[#8b2a5e] focus-visible:ring-2 focus-visible:ring-[#b34f86]"
      >
        <CircleHelp size={14} aria-hidden="true" />
      </button>
      {isOpen ? (
        <span
          role="status"
          style={tooltipPosition}
          className={`fixed z-50 rounded-lg bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-5 text-white shadow-lg ${placement === "top" ? "-translate-y-full" : ""}`}
        >
          {content}
        </span>
      ) : null}
    </span>
  );
};

const EventLabel = ({
  event,
  actor,
  metric,
  placement,
  className = "",
}: {
  event: MetrikaEvent;
  actor?: AnalyticsActorType;
  metric?: MetricKey;
  placement?: "top" | "bottom";
  className?: string;
}) => {
  return (
    <span className={`inline-flex min-w-0 items-center gap-1 ${className}`}>
      <span className="truncate">{getEventLabel(event)}</span>
      <ClickTooltip
        placement={placement}
        label={`Показать описание события «${getEventLabel(event)}»`}
        content={
          <>
            <span className="block">{getEventDescription(event)}</span>
            {actor && metric ? (
              <span className="mt-1 block border-t border-slate-700 pt-1 text-slate-200">
                {METRIC_LABELS[metric]} · {ACTOR_LABELS[actor]}: {" "}
                <strong className="font-semibold text-white">
                  {numberFormat.format(eventMetric(event, actor, metric))}
                </strong>
              </span>
            ) : null}
          </>
        }
      />
    </span>
  );
};

type EventsCatalogItem = {
  name: string;
  label: string;
  description: string;
  eventCount: number;
};

const EventsCatalogModal = ({
  open,
  events,
  actor,
  onClose,
}: {
  open: boolean;
  events: EventsCatalogItem[];
  actor: AnalyticsActorType;
  onClose: () => void;
}) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, open]);

  const visibleEvents = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("ru");
    if (!query) return events;
    return events.filter((event) =>
      `${event.label} ${event.description} ${event.name}`
        .toLocaleLowerCase("ru")
        .includes(query),
    );
  }, [events, search]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4"
      onMouseDown={onClose}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="events-catalog-title"
        className="flex max-h-[min(720px,calc(100vh-2rem))] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-5 border-b border-slate-200 px-5 py-4 sm:px-6">
          <div>
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.12em] text-[#a34a7a]">
              Справочник
            </p>
            <h3
              id="events-catalog-title"
              className="m-0 mt-1 text-lg font-semibold text-slate-950"
            >
              Все известные события
            </h3>
            <p className="m-0 mt-1 text-xs leading-5 text-slate-500">
              В списке есть и события, которые не выполнялись в выбранном
              периоде.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть каталог событий"
            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b34f86]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="border-b border-slate-100 px-5 py-3 sm:px-6">
          <input
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Поиск по названию или описанию"
            className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-[#b34f86] focus:ring-2 focus:ring-[#efd5e3]"
          />
        </div>
        <div className="overflow-y-auto px-5 py-2 sm:px-6">
          {visibleEvents.map((event) => (
            <div
              key={event.name}
              className="flex items-start justify-between gap-5 border-b border-slate-100 py-3 last:border-b-0"
            >
              <div className="min-w-0">
                <p className="m-0 text-sm font-semibold text-slate-900">
                  {event.label}
                </p>
                <p className="m-0 mt-0.5 text-xs leading-5 text-slate-500">
                  {event.description}
                </p>
                <p className="m-0 mt-1 font-mono text-[10px] text-slate-400">
                  {event.name}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium ${event.eventCount ? "bg-[#fff1f7] text-[#8b2a5e]" : "bg-slate-100 text-slate-500"}`}
              >
                {event.eventCount
                  ? `${numberFormat.format(event.eventCount)} событий`
                  : "Нет за период"}
              </span>
            </div>
          ))}
          {!visibleEvents.length ? (
            <p className="m-0 py-10 text-center text-sm text-slate-500">
              События не найдены.
            </p>
          ) : null}
        </div>
        <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500 sm:px-6">
          Количество отражает сегмент «{ACTOR_LABELS[actor]}» за выбранный
          период.
        </div>
      </section>
    </div>
  );
};

const formatPeriod = (summary: MetrikaSummary) => {
  const format = new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
  });
  const from = new Date(`${summary.period.date_from}T12:00:00`);
  const to = new Date(`${summary.period.date_to}T12:00:00`);
  return `${format.format(from)} — ${new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric" }).format(to)}`;
};

const formatUpdatedAt = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
};

const getErrorMessage = (error: any) => {
  if (error?.response?.status === 401)
    return "Требуется авторизация менеджера.";
  if (error?.response?.status === 403)
    return "Этот раздел доступен только менеджерам и администраторам.";
  if (error?.response?.status === 404)
    return "Готовой сводки за этот период пока нет. Нажмите «Получить актуальные данные».";
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    "Не удалось получить данные Метрики."
  );
};

const getPeriodDayQuantity = (period: { date_from: string; date_to: string }) =>
  Math.floor(
    (Date.parse(`${period.date_to}T00:00:00Z`) -
      Date.parse(`${period.date_from}T00:00:00Z`)) /
      86_400_000,
  ) + 1;

const parseAnalyticsDate = (value: string) => {
  const dateParts = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateParts) {
    const [, year, month, day] = dateParts;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const chartDateFormat = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

const formatChartDate = (value: string) => {
  const date = parseAnalyticsDate(value);
  return date ? chartDateFormat.format(date).replace(".", "") : "—";
};

const chartTooltipDateFormat = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

const formatChartTooltipDate = (value: string) => {
  const date = parseAnalyticsDate(value);
  return date ? chartTooltipDateFormat.format(date) : "Дата не указана";
};

const MetricCard = ({
  icon,
  value,
  label,
  hint,
  description,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  hint: string;
  description: string;
}) => (
  <div className="border-t border-slate-200 pt-3">
    <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
      {icon}
      {label}
      <ClickTooltip label={`Что означает «${label}»`} content={description} />
    </div>
    <div className="mt-2 text-[30px] font-semibold tracking-[-0.045em] text-slate-950 tabular-nums">
      {numberFormat.format(value)}
    </div>
    <p className="m-0 mt-1 text-xs leading-4 text-slate-500">{hint}</p>
  </div>
);

const ActivityTrend = ({
  actor,
  points,
}: {
  actor: AnalyticsActorType;
  points: MetrikaSummary["daily_dynamics"];
}) => {
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(
    null,
  );
  const [selectedDailyEvent, setSelectedDailyEvent] =
    useState<MetrikaDailyEvent | null>(null);
  const width = 760;
  const height = 236;
  const margin = { top: 16, right: 18, bottom: 38, left: 54 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  const values = points.map((item) => item[actor]);
  const max = Math.max(...values, 1);
  const y = (value: number) =>
    margin.top + chartHeight - (value / max) * chartHeight;
  const x = (index: number) =>
    margin.left + (index / Math.max(points.length - 1, 1)) * chartWidth;
  const path = points
    .map((item, index) => `${index ? "L" : "M"}${x(index)},${y(item[actor])}`)
    .join(" ");
  const yTicks = [0, 0.5, 1].map((part) => Math.round(max * part));

  const hoveredPoint =
    hoveredPointIndex === null ? null : points[hoveredPointIndex];
  const hoveredPointX =
    hoveredPointIndex === null ? 0 : (x(hoveredPointIndex) / width) * 100;
  const hoveredPointY = hoveredPoint ? (y(hoveredPoint[actor]) / height) * 100 : 0;
  const isFirstPoint = hoveredPointIndex === 0;
  const isLastPoint = hoveredPointIndex === points.length - 1;
  const showTooltipBelow = hoveredPoint !== null && y(hoveredPoint[actor]) < 82;
  const hoveredEvents = hoveredPoint
    ? [...(hoveredPoint.events || [])]
        .filter((event) => event[actor] > 0)
        .sort((left, right) => right[actor] - left[actor])
    : [];
  const setHoveredPointFromCursor = (
    event: React.MouseEvent<SVGSVGElement>,
  ) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const cursorX = ((event.clientX - bounds.left) / bounds.width) * width;
    const index = Math.round(
      ((cursorX - margin.left) / chartWidth) * Math.max(points.length - 1, 1),
    );
    const nextIndex = Math.max(0, Math.min(points.length - 1, index));
    setHoveredPointIndex((currentIndex) =>
      currentIndex === nextIndex ? currentIndex : nextIndex,
    );
    setSelectedDailyEvent(null);
  };

  return (
    <div
      className="relative"
      onMouseLeave={() => {
        setHoveredPointIndex(null);
        setSelectedDailyEvent(null);
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block w-full overflow-visible"
        role="img"
        aria-label={`Динамика событий: ${ACTOR_LABELS[actor]}`}
        onMouseMove={setHoveredPointFromCursor}
      >
      <rect
        x={margin.left}
        y={margin.top}
        width={chartWidth}
        height={chartHeight}
        fill="none"
        stroke="#e2e8f0"
      />
      {yTicks.map((tick) => (
        <g key={tick}>
          <line
            x1={margin.left}
            x2={width - margin.right}
            y1={y(tick)}
            y2={y(tick)}
            stroke="#e2e8f0"
            strokeDasharray={tick === 0 ? "0" : "3 4"}
          />
          <text
            x={margin.left - 10}
            y={y(tick) + 4}
            textAnchor="end"
            fill="#64748b"
            fontSize="11"
          >
            {numberFormat.format(tick)}
          </text>
        </g>
      ))}
      {points.map((point, index) =>
        index === 0 || index === points.length - 1 || index % 3 === 0 ? (
          <text
            key={point.date}
            x={x(index)}
            y={height - 12}
            textAnchor="middle"
            fill="#64748b"
            fontSize="11"
          >
            {formatChartDate(point.date)}
          </text>
        ) : null,
      )}
      {hoveredPointIndex !== null ? (
        <line
          x1={x(hoveredPointIndex)}
          x2={x(hoveredPointIndex)}
          y1={margin.top}
          y2={margin.top + chartHeight}
          stroke="#cbd5e1"
          strokeDasharray="3 4"
        />
      ) : null}
      <path d={path} fill="none" stroke="#b34f86" strokeWidth="2.5" />
        {points.map((point, index) => (
          <g
            key={point.date}
          >
            <circle
              cx={x(index)}
              cy={y(point[actor])}
              r="7"
              fill="transparent"
            />
            <circle cx={x(index)} cy={y(point[actor])} r="3.5" fill="#b34f86" />
          </g>
        ))}
      <text x={margin.left} y={11} fill="#0f172a" fontSize="12">
        события
      </text>
      </svg>
      {hoveredPoint ? (
        <div
          className={`absolute z-10 w-64 max-w-[calc(100vw-2rem)] rounded-lg bg-slate-900 px-2.5 py-2 text-[11px] leading-4 text-white shadow-lg ${isFirstPoint ? "translate-x-0" : isLastPoint ? "-translate-x-full" : "-translate-x-1/2"} ${showTooltipBelow ? "translate-y-2" : "-translate-y-[calc(100%+8px)]"}`}
          style={{ left: `${hoveredPointX}%`, top: `${hoveredPointY}%` }}
        >
          <p className="m-0 text-xs font-semibold">
            {formatChartTooltipDate(hoveredPoint.date)}
          </p>
          <p className="m-0 mt-1 border-t border-slate-700 pt-1 text-slate-200">
            События · {ACTOR_LABELS[actor]}: {" "}
            <strong className="font-semibold text-white">
              {numberFormat.format(hoveredPoint[actor])}
            </strong>
          </p>
          <div className="mt-1.5 border-t border-slate-700 pt-1.5">
            <p className="m-0 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
              Действия за день
            </p>
            {hoveredEvents.length ? (
              <div className="mt-1 space-y-0.5">
                {hoveredEvents.map((event) => (
                  <div
                    key={event.name}
                    className="flex items-start justify-between gap-2 py-0.5"
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedDailyEvent(event)}
                      className="min-w-0 truncate text-left text-slate-200 underline-offset-2 transition hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white"
                    >
                        {getEventLabel(event)}
                    </button>
                    <strong className="shrink-0 font-semibold text-white">
                      {numberFormat.format(event[actor])}
                    </strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="m-0 mt-1 text-slate-400">
                Детализация событий для этого дня пока отсутствует.
              </p>
            )}
          </div>
          {selectedDailyEvent ? (
            <div className="mt-1.5 border-t border-slate-700 pt-1.5 text-slate-300">
              <div className="flex items-start justify-between gap-2">
                <span className="font-semibold text-white">
                  {getEventLabel(selectedDailyEvent)}
                </span>
                <button
                  type="button"
                  aria-label="Закрыть описание события"
                  onClick={() => setSelectedDailyEvent(null)}
                  className="shrink-0 text-slate-400 transition hover:text-white"
                >
                  ×
                </button>
              </div>
              <p className="m-0 mt-1 text-[10px] leading-3.5">
                {getEventDescription(selectedDailyEvent)}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const Funnel = ({
  title,
  events,
  actor,
  metric,
  source,
  errorEvents = [],
}: {
  title: string;
  events: string[];
  actor: AnalyticsActorType;
  metric: MetricKey;
  source: MetrikaEvent[];
  errorEvents?: string[];
}) => {
  const rows = events
    .map(
      (name) =>
        source.find((item) => item.name === name) || {
          name,
          all: { events: 0, unique_visitors: 0, unique_visits: 0 },
          authenticated: { events: 0, unique_visitors: 0, unique_visits: 0 },
          anonymous: { events: 0, unique_visitors: 0, unique_visits: 0 },
        },
    ) as MetrikaEvent[];
  const baseline = Math.max(eventMetric(rows[0], actor, metric), 1);
  return (
    <section className="border-t border-slate-200 pt-4">
      <h3 className="m-0 text-sm font-semibold text-slate-950">{title}</h3>
      <div className="mt-4 space-y-3">
        {rows.map((item, index) => {
          const value = eventMetric(item, actor, metric);
          return (
            <div
              key={item.name}
              className="grid grid-cols-[minmax(120px,1fr)_minmax(80px,1.7fr)_76px] items-center gap-3 text-xs"
            >
              <EventLabel
                event={item}
                actor={actor}
                metric={metric}
                className="text-slate-600"
              />
              <div className="h-2.5 overflow-hidden bg-slate-100">
                <div
                  className={
                    errorEvents.includes(item.name)
                      ? "h-full bg-rose-500"
                      : index === 0
                      ? "h-full bg-[#b34f86]"
                      : index === 1
                        ? "h-full bg-[#7b78d7]"
                        : "h-full bg-[#36a78a]"
                  }
                  style={{ width: `${Math.min((value / baseline) * 100, 100)}%` }}
                />
              </div>
              <span className="text-right font-semibold tabular-nums text-slate-800">
                {numberFormat.format(value)} · {((value / baseline) * 100).toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const AnalyticsPanel = () => {
  const [actor, setActor] = useState<AnalyticsActorType>("all");
  const [category, setCategory] = useState<Category>("all");
  const [metric, setMetric] = useState<MetricKey>("events");
  const [query, setQuery] = useState("");
  const [isEventsCatalogOpen, setIsEventsCatalogOpen] = useState(false);
  const [summary, setSummary] = useState<MetrikaSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [periodLimits, setPeriodLimits] =
    useState<MetrikaPeriodLimits | null>(null);
  const [limitsLoading, setLimitsLoading] = useState(true);
  const [periodLimitsError, setPeriodLimitsError] = useState<string | null>(
    null,
  );
  const refreshTimerRef = useRef<number | null>(null);
  const limitsRequestRef = useRef(0);
  const summaryRequestRef = useRef(0);

  const selectedPeriod = useMemo(
    () =>
      dateFrom && dateTo
        ? { date_from: dateFrom, date_to: dateTo }
        : null,
    [dateFrom, dateTo],
  );

  const selectedPeriodDayQuantity = useMemo(
    () => (selectedPeriod ? getPeriodDayQuantity(selectedPeriod) : 0),
    [selectedPeriod],
  );

  const periodValidationMessage = useMemo(() => {
    if (!selectedPeriod) return "Укажите дату начала и окончания периода.";
    if (selectedPeriod.date_from > selectedPeriod.date_to)
      return "Дата окончания не может быть раньше даты начала.";
    if (
      periodLimits &&
      selectedPeriodDayQuantity > periodLimits.max_days_per_request
    )
      return `В одном запросе доступно не более ${numberFormat.format(periodLimits.max_days_per_request)} дн.`;
    if (
      periodLimits &&
      (selectedPeriod.date_from < periodLimits.min_date ||
        selectedPeriod.date_to > periodLimits.max_date)
    )
      return `Доступный период: ${periodLimits.min_date} — ${periodLimits.max_date}.`;
    if (periodLimits && !periodLimits.possible)
      return `На эту выгрузку пока не хватает квоты Метрики. Сейчас доступно до ${numberFormat.format(periodLimits.max_possible_day_quantity)} дн.`;
    return periodLimitsError;
  }, [
    periodLimits,
    periodLimitsError,
    selectedPeriod,
    selectedPeriodDayQuantity,
  ]);

  const stopPolling = useCallback(() => {
    if (refreshTimerRef.current !== null)
      window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = null;
  }, []);

  const loadPeriodLimits = useCallback(
    async (
      period?: { date_from: string; date_to: string },
      initializePeriod = false,
    ) => {
      const requestId = ++limitsRequestRef.current;
      setLimitsLoading(true);
      setPeriodLimitsError(null);
      try {
        const response = await apiClient.get<{ data: MetrikaPeriodLimits }>(
          API_ENDPOINTS.metrikaAnalyticsPeriodLimits,
          {
            headers: JSON_HEADERS,
            params: period,
          },
        );
        if (requestId !== limitsRequestRef.current) return;
        const limits = response.data.data;
        setPeriodLimits(limits);
        if (initializePeriod) {
          setDateFrom(limits.period.date_from);
          setDateTo(limits.period.date_to);
        }
      } catch (requestError) {
        if (requestId !== limitsRequestRef.current) return;
        setPeriodLimitsError(getErrorMessage(requestError));
      } finally {
        if (requestId === limitsRequestRef.current) setLimitsLoading(false);
      }
    },
    [],
  );

  const loadSummary = useCallback(
    async (
      targetActor: AnalyticsActorType,
      period: { date_from: string; date_to: string },
    ) => {
      const requestId = ++summaryRequestRef.current;
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get<{ data: MetrikaSummary }>(
          API_ENDPOINTS.metrikaAnalytics,
          { headers: JSON_HEADERS, params: { segment: targetActor, ...period } },
        );
        if (requestId !== summaryRequestRef.current) return;
        setSummary(response.data.data);
      } catch (requestError) {
        if (requestId !== summaryRequestRef.current) return;
        setSummary(null);
        setError(getErrorMessage(requestError));
      } finally {
        if (requestId === summaryRequestRef.current) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (
      selectedPeriod &&
      selectedPeriod.date_from <= selectedPeriod.date_to
    )
      void loadSummary(actor, selectedPeriod);
  }, [actor, loadSummary, selectedPeriod]);
  useEffect(() => {
    void loadPeriodLimits(undefined, true);
  }, [loadPeriodLimits]);
  useEffect(() => {
    if (
      selectedPeriod &&
      selectedPeriod.date_from <= selectedPeriod.date_to
    )
      void loadPeriodLimits(selectedPeriod);
  }, [loadPeriodLimits, selectedPeriod]);
  useEffect(() => () => stopPolling(), [stopPolling]);

  const pollRefresh = useCallback(
    async (refreshId: string, targetActor: AnalyticsActorType) => {
      try {
        const response = await apiClient.get<{ data: RefreshStatusData }>(
          API_ENDPOINTS.metrikaAnalyticsRefreshStatus(refreshId),
          { headers: JSON_HEADERS, params: { segment: targetActor } },
        );
        const job = response.data.data;
        if (job.status === "completed" && job.summary) {
          stopPolling();
          setSummary(job.summary);
          setError(null);
          setRefreshing(false);
          setLoading(false);
          return;
        }
        if (job.status === "failed") {
          stopPolling();
          setRefreshing(false);
          setError(job.message || "Не удалось обновить данные Метрики.");
          return;
        }
        refreshTimerRef.current = window.setTimeout(() => {
          void pollRefresh(refreshId, targetActor);
        }, 3000);
      } catch (requestError) {
        stopPolling();
        setRefreshing(false);
        setError(getErrorMessage(requestError));
      }
    },
    [stopPolling],
  );

  const refresh = async () => {
    if (!selectedPeriod || periodValidationMessage || limitsLoading) return;
    stopPolling();
    setRefreshing(true);
    setError(null);
    try {
      const response = await apiClient.post<{ data: RefreshStatusData }>(
        API_ENDPOINTS.metrikaAnalyticsRefresh,
        selectedPeriod,
        { headers: JSON_HEADERS },
      );
      await pollRefresh(response.data.data.refresh_id, actor);
    } catch (requestError) {
      setRefreshing(false);
      setError(getErrorMessage(requestError));
    }
  };

  const filteredEvents = useMemo(
    () =>
      summary?.events.filter((item) => {
        const inCategory = category === "all" || categoryOf(item) === category;
        return (
          inCategory &&
          getEventLabel(item)
            .toLocaleLowerCase("ru")
            .includes(query.trim().toLocaleLowerCase("ru"))
        );
      }) || [],
    [category, query, summary],
  );
  const ranking = useMemo(
    () =>
      filteredEvents
        .filter((item) => eventMetric(item, actor, metric) > 0)
        .sort(
          (left, right) =>
            eventMetric(right, actor, metric) -
            eventMetric(left, actor, metric),
        )
        .slice(0, 10),
    [actor, filteredEvents, metric],
  );
  const rankingMax = Math.max(
    ...ranking.map((item) => eventMetric(item, actor, metric)),
    1,
  );
  const composition = useMemo(
    () =>
      (summary?.events || [])
        .filter(
          (item) =>
            (category === "all" || categoryOf(item) === category) &&
            item.all.events > 0,
        )
        .sort((left, right) => right.all.events - left.all.events)
        .slice(0, 8),
    [category, summary],
  );
  const catalogEvents = useMemo(() => {
    const eventsByName = new Map(
      (summary?.events || []).map((event) => [event.name, event]),
    );
    const eventNames = new Set([
      ...Object.values(ANALYTICS_EVENTS),
      ...eventsByName.keys(),
    ]);

    return Array.from(eventNames)
      .map((name) => {
        const event = eventsByName.get(name);
        return {
          name,
          label: getEventLabel({ name, label: event?.label }),
          description: getEventDescription({
            name,
            label: event?.label,
            description: event?.description,
          }),
          eventCount: event ? event[actor].events : 0,
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label, "ru"));
  }, [actor, summary]);

  return (
    <div className="px-4 pb-2 sm:px-0">
      <section className="border-y border-slate-200 bg-white px-4 py-5 sm:rounded-2xl sm:border sm:px-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-[#a34a7a]">
              Яндекс.Метрика
            </p>
            <h3 className="m-0 mt-1 text-xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-2xl">
              Продуктовая аналитика
            </h3>
            <p className="m-0 mt-2 text-sm text-slate-500">
              {summary
                ? `${formatPeriod(summary)} · обновлено ${formatUpdatedAt(summary.updated_at)}`
                : "Загружаем последнюю сводку…"}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-end">
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-medium text-slate-600">
                Начало периода
                <input
                  type="date"
                  value={dateFrom}
                  min={periodLimits?.min_date}
                  max={periodLimits?.max_date}
                  disabled={refreshing || limitsLoading}
                  onChange={(event) => setDateFrom(event.target.value)}
                  className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#b34f86] focus:ring-2 focus:ring-[#efd5e3] disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>
              <label className="text-xs font-medium text-slate-600">
                Конец периода
                <input
                  type="date"
                  value={dateTo}
                  min={periodLimits?.min_date}
                  max={periodLimits?.max_date}
                  disabled={refreshing || limitsLoading}
                  onChange={(event) => setDateTo(event.target.value)}
                  className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#b34f86] focus:ring-2 focus:ring-[#efd5e3] disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>
            </div>
              <label className="text-xs font-medium text-slate-600">
                  <span className="flex items-center gap-1">
                    Сегмент
                    <ClickTooltip
                      placement="bottom"
                      label="Что означает выбранный сегмент"
                      content={ACTOR_DESCRIPTIONS[actor]}
                    />
                  </span>
              <select
                value={actor}
                disabled={refreshing}
                onChange={(event) =>
                  setActor(event.target.value as AnalyticsActorType)
                }
                className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#b34f86] focus:ring-2 focus:ring-[#efd5e3] disabled:opacity-60"
              >
                <option value="all">Все</option>
                <option value="authenticated">Залогиненные</option>
                <option value="anonymous">Незалогиненные</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={
                refreshing || limitsLoading || Boolean(periodValidationMessage)
              }
              className="inline-flex h-[38px] items-center justify-center gap-2 rounded-lg bg-[#8b2a5e] px-4 text-sm font-semibold text-white transition hover:bg-[#6e2048] disabled:cursor-wait disabled:bg-[#c796ae]"
            >
              <RefreshCw
                size={16}
                className={refreshing ? "animate-spin" : ""}
              />
              {refreshing ? "Обновляем…" : "Получить актуальные данные"}
            </button>
          </div>
        </div>
        <div className="mt-5 flex flex-col gap-2 border-t border-slate-100 pt-4 text-xs leading-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="m-0 text-slate-500">
            {limitsLoading
              ? "Проверяем допустимый период и квоту Метрики…"
              : `За один запрос можно выгрузить до ${numberFormat.format(periodLimits?.max_days_per_request || 365)} дн.`}
          </p>
          {periodValidationMessage ? (
            <p className="m-0 font-medium text-amber-700">
              {periodValidationMessage}
            </p>
          ) : periodLimits ? (
            <p className="m-0 font-medium text-emerald-700">
              Период доступен по текущей квоте Метрики.
            </p>
          ) : null}
        </div>
        {summary?.status === "updating" || refreshing ? (
          <p className="mb-0 mt-5 flex items-center gap-2 border-t border-amber-100 pt-4 text-xs text-amber-700">
            <RefreshCw size={14} className="animate-spin" />
            Метрика готовит новую выгрузку; страница обновится автоматически.
          </p>
        ) : null}
        {summary?.status === "error" ? (
          <p className="mb-0 mt-5 flex items-center gap-2 border-t border-rose-100 pt-4 text-xs text-rose-700">
            <AlertCircle size={14} />
            Последнее обновление завершилось ошибкой. Запустите новую выгрузку.
          </p>
        ) : null}
      </section>

      {error ? (
        <section className="mt-7 flex items-start gap-3 border-y border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-800 sm:rounded-2xl sm:border sm:px-6">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="m-0 font-semibold">Данные пока недоступны</p>
            <p className="m-0 mt-1 leading-5">{error}</p>
          </div>
        </section>
      ) : null}
      {loading && !summary ? (
        <section className="mt-7 border-y border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500 sm:rounded-2xl sm:border">
          <RefreshCw
            size={18}
            className="mx-auto mb-3 animate-spin text-[#a34a7a]"
          />
          Загружаем сводку Метрики…
        </section>
      ) : null}

      {summary ? (
        <>
          <section className="mt-7 border-y border-slate-200 bg-white px-4 py-5 sm:rounded-2xl sm:border sm:px-6">
            <div className="grid grid-cols-2 gap-x-6 gap-y-5 lg:grid-cols-4">
              <MetricCard
                icon={<MousePointer2 size={15} />}
                value={summary.totals.events}
                label="События"
                hint="все зафиксированные действия"
                description={METRIC_DESCRIPTIONS.events}
              />
              <MetricCard
                icon={<Users size={15} />}
                value={summary.totals.unique_visitors}
                label="Уникальные посетители"
                hint="браузерные ID Метрики"
                description={METRIC_DESCRIPTIONS.visitors}
              />
              <MetricCard
                icon={<Activity size={15} />}
                value={summary.totals.unique_visits}
                label="Уникальные визиты"
                hint="сессии с продуктовым действием"
                description={METRIC_DESCRIPTIONS.visits}
              />
              <MetricCard
                icon={<Filter size={15} />}
                value={summary.totals.active_actions}
                label="Активные действия"
                hint="названия событий в сегменте"
                description="Сколько разных типов действий произошло хотя бы один раз в выбранных периоде и сегменте."
              />
            </div>
          </section>

          <section className="mt-7 grid gap-7 xl:grid-cols-[minmax(0,1.65fr)_minmax(280px,0.85fr)]">
            <div className="border-y border-slate-200 bg-white px-4 py-5 sm:rounded-2xl sm:border sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-1">
                    <h3 className="m-0 text-base font-semibold text-slate-950">
                      Динамика действий
                    </h3>
                    <ClickTooltip
                      label="Что показывает динамика действий"
                      content={METRIC_DESCRIPTIONS.events}
                    />
                  </div>
                  <p className="m-0 mt-1 text-xs leading-5 text-slate-500">
                    Все продуктовые события за день для сегмента «
                    {ACTOR_LABELS[actor]}».
                  </p>
                </div>
                <CalendarDays size={18} className="shrink-0 text-slate-400" />
              </div>
              <div className="mt-5">
                <ActivityTrend actor={actor} points={summary.daily_dynamics} />
              </div>
            </div>
            <div className="border-y border-slate-200 bg-white px-4 py-5 sm:rounded-2xl sm:border sm:px-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={18} className="mt-0.5 text-[#a34a7a]" />
                <div>
                  <h3 className="m-0 text-base font-semibold text-slate-950">
                    Статус данных
                  </h3>
                  <p className="m-0 mt-1 text-xs leading-5 text-slate-500">
                    Сводка возвращается backend после агрегирования событий.
                  </p>
                </div>
              </div>
              <dl className="mt-6 space-y-4 border-t border-slate-100 pt-4 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Выбранный сегмент</dt>
                  <dd className="m-0 font-semibold text-slate-900">
                    {ACTOR_LABELS[actor]}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Статус</dt>
                  <dd className="m-0 font-semibold text-emerald-700">
                    {summary.status === "actual"
                      ? "Актуальные"
                      : summary.status === "updating"
                        ? "Обновляются"
                        : "Ошибка"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Последнее обновление</dt>
                  <dd className="m-0 text-right font-semibold text-slate-900">
                    {formatUpdatedAt(summary.updated_at)}
                  </dd>
                </div>
              </dl>
              <p className="mb-0 mt-6 border-t border-slate-100 pt-4 text-xs leading-5 text-slate-500">
                Один браузер может войти в аккаунт в течение периода, поэтому
                уникальные посетители двух сегментов не суммируются.
              </p>
            </div>
          </section>

          <section className="mt-7 border-y border-slate-200 bg-white px-4 py-5 sm:rounded-2xl sm:border sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="m-0 text-base font-semibold text-slate-950">
                  Лидирующие действия
                </h3>
                <p className="m-0 mt-1 text-xs leading-5 text-slate-500">
                  {CATEGORY_LABELS[category]} · по показателю «
                  {METRIC_LABELS[metric].toLocaleLowerCase("ru")}».
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-medium text-slate-600">
                  <span className="flex items-center gap-1">
                    Группа
                    <ClickTooltip
                      placement="bottom"
                      label="Что делает фильтр группы"
                      content="Оставляет в рейтинге только связанные действия: расчёты, маркет, вход и регистрация, оплата или системные события."
                    />
                  </span>
                  <select
                    value={category}
                    onChange={(event) =>
                      setCategory(event.target.value as Category)
                    }
                    className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#b34f86] focus:ring-2 focus:ring-[#efd5e3]"
                  >
                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-medium text-slate-600">
                  Сортировка
                  <ClickTooltip
                    placement="bottom"
                    label="Что означает выбранный показатель"
                    content={METRIC_DESCRIPTIONS[metric]}
                  />
                  <select
                    value={metric}
                    onChange={(event) =>
                      setMetric(event.target.value as MetricKey)
                    }
                    className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#b34f86] focus:ring-2 focus:ring-[#efd5e3]"
                  >
                    {Object.entries(METRIC_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            <div className="mt-5 space-y-3">
              {ranking.map((item) => {
                const value = eventMetric(item, actor, metric);
                return (
                  <div
                    key={item.name}
                    className="grid grid-cols-[minmax(130px,1fr)_minmax(100px,2.3fr)_52px] items-center gap-3 text-sm"
                  >
                    <EventLabel
                      event={item}
                      actor={actor}
                      metric={metric}
                      className="text-slate-700"
                    />
                    <div className="h-3 bg-slate-100">
                      <div
                        className="h-full bg-[#b34f86]"
                        style={{ width: `${(value / rankingMax) * 100}%` }}
                      />
                    </div>
                    <span className="text-right font-semibold tabular-nums text-slate-900">
                      {numberFormat.format(value)}
                    </span>
                  </div>
                );
              })}
              {!ranking.length ? (
                <p className="m-0 py-8 text-center text-sm text-slate-500">
                  В выбранном срезе нет событий.
                </p>
              ) : null}
            </div>
          </section>

          <section className="mt-7 grid gap-7 xl:grid-cols-2">
            <div className="border-y border-slate-200 bg-white px-4 py-5 sm:rounded-2xl sm:border sm:px-6">
              <div className="flex items-center gap-1">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Конверсии связанных событий
                </p>
                <ClickTooltip
                  label="Как читать конверсии"
                  content="Каждая строка показывает долю выбранного показателя от первого шага сценария. Ошибки выделены красным и не являются следующим обязательным шагом."
                />
              </div>
              <div className="mt-5 grid gap-7">
                <Funnel
                  title="Расчёты и шаринг"
                  actor={actor}
                  metric={metric}
                  source={summary.events}
                  events={[
                    "calculation_ready",
                    "calculation_share_requested",
                    "calculation_share_created",
                    "calculation_link_copied",
                  ]}
                />
                <Funnel
                  title="Маркет"
                  actor={actor}
                  metric={metric}
                  source={summary.events}
                  events={[
                    "market_opened",
                    "market_listing_viewed",
                    "market_contact_clicked",
                  ]}
                />
                <Funnel
                  title="Создание объявления"
                  actor={actor}
                  metric={metric}
                  source={summary.events}
                  events={[
                    "market_listing_create_started",
                    "market_listing_created",
                    "market_listing_create_failed",
                  ]}
                  errorEvents={["market_listing_create_failed"]}
                />
              </div>
            </div>
            <div className="border-y border-slate-200 bg-white px-4 py-5 sm:rounded-2xl sm:border sm:px-6">
              <div className="flex items-center gap-1">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Структура аудитории
                </p>
                <ClickTooltip
                  label="Как читать структуру аудитории"
                  content="Для каждого действия показано, какая часть всех его событий пришлась на залогиненных и незалогиненных посетителей."
                />
              </div>
              <h3 className="m-0 mt-2 text-base font-semibold text-slate-950">
                Кто выполняет действия
              </h3>
              <p className="m-0 mt-1 text-xs leading-5 text-slate-500">
                Доля событий по основным действиям. Розовый — залогиненные,
                фиолетовый — незалогиненные.
              </p>
              <div className="mt-6 space-y-4">
                {composition.map((item) => {
                  const anonymousShare =
                    (item.anonymous.events / item.all.events) * 100;
                  return (
                    <div key={item.name}>
                      <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
                        <EventLabel
                          event={item}
                          actor="all"
                          metric="events"
                          className="text-slate-600"
                        />
                        <span className="shrink-0 font-medium tabular-nums text-slate-800">
                          {numberFormat.format(item.all.events)}
                        </span>
                      </div>
                      <div className="flex h-2.5 overflow-hidden bg-slate-100">
                        <div
                          className="bg-[#b34f86]"
                          style={{ width: `${100 - anonymousShare}%` }}
                        />
                        <div
                          className="bg-[#7b78d7]"
                          style={{ width: `${anonymousShare}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="mt-7 border-y border-slate-200 bg-white sm:rounded-2xl sm:border">
            <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-6">
              <div>
                <h3 className="m-0 text-base font-semibold text-slate-950">
                  Полная детализация
                </h3>
                <p className="m-0 mt-1 text-xs leading-5 text-slate-500">
                  Значения уникальных посетителей и визитов считаются внутри
                  каждого действия.
                </p>
                <button
                  type="button"
                  onClick={() => setIsEventsCatalogOpen(true)}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-[#d39ab7] hover:bg-[#fff8fc] hover:text-[#8b2a5e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b34f86]"
                >
                  Посмотреть все события
                  <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                    {catalogEvents.length}
                  </span>
                </button>
              </div>
              <label className="text-xs font-medium text-slate-600">
                Поиск действия
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Например, маркет"
                  className="mt-1.5 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-[#b34f86] focus:ring-2 focus:ring-[#efd5e3] sm:w-64"
                />
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[780px] w-full text-left text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium text-slate-500">
                  <tr>
                    <th className="px-4 py-3 sm:px-6">
                      <span className="inline-flex items-center gap-1">
                        Действие
                        <ClickTooltip
                          placement="bottom"
                          label="Что означает действие"
                          content="Название действия пользователя, которое сервис передал в Метрику."
                        />
                      </span>
                    </th>
                    <th className="px-3 py-3 text-right">
                      <span className="inline-flex items-center gap-1">
                        События
                        <ClickTooltip
                          placement="bottom"
                          label="Что означает количество событий"
                          content={METRIC_DESCRIPTIONS.events}
                        />
                      </span>
                    </th>
                    <th className="px-3 py-3 text-right">
                      <span className="inline-flex items-center gap-1">
                        Уникальные посетители
                        <ClickTooltip
                          placement="bottom"
                          label="Что означает количество уникальных посетителей"
                          content={METRIC_DESCRIPTIONS.visitors}
                        />
                      </span>
                    </th>
                    <th className="px-3 py-3 text-right">
                      <span className="inline-flex items-center gap-1">
                        Уникальные визиты
                        <ClickTooltip
                          placement="bottom"
                          label="Что означает количество уникальных визитов"
                          content={METRIC_DESCRIPTIONS.visits}
                        />
                      </span>
                    </th>
                    <th className="px-4 py-3 text-right sm:px-6">
                      <span className="inline-flex items-center gap-1">
                        Доля событий
                        <ClickTooltip
                          placement="bottom"
                          label="Что означает доля событий"
                          content="Какую часть всех событий выбранного сегмента составляет это действие за период."
                        />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredEvents
                    .filter((item) => item[actor].events > 0)
                    .sort(
                      (left, right) => right[actor].events - left[actor].events,
                    )
                    .map((item) => (
                      <tr
                        key={item.name}
                        className="text-slate-700 hover:bg-[#fff8fc]"
                      >
                        <td className="px-4 py-3.5 font-medium text-slate-900 sm:px-6">
                          <EventLabel
                            event={item}
                            actor={actor}
                            metric="events"
                            placement="bottom"
                          />
                        </td>
                        <td className="px-3 py-3.5 text-right tabular-nums">
                          {numberFormat.format(item[actor].events)}
                        </td>
                        <td className="px-3 py-3.5 text-right tabular-nums">
                          {numberFormat.format(item[actor].unique_visitors)}
                        </td>
                        <td className="px-3 py-3.5 text-right tabular-nums">
                          {numberFormat.format(item[actor].unique_visits)}
                        </td>
                        <td className="px-4 py-3.5 text-right tabular-nums text-slate-500 sm:px-6">
                          {(
                            (item[actor].events /
                              Math.max(summary.totals.events, 1)) *
                            100
                          )
                            .toFixed(1)
                            .replace(".", ",")}
                          %
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
          <EventsCatalogModal
            open={isEventsCatalogOpen}
            events={catalogEvents}
            actor={actor}
            onClose={() => setIsEventsCatalogOpen(false)}
          />
        </>
      ) : null}
    </div>
  );
};
